import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Spinner,
  VStack,
  HStack,
  Badge,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import {
  CreateAssessmentModal,
  EvidenceReviewModal,
  PdfViewerModal,
} from "@root/components/modals";
import { CitationModal } from "@root/components/LegalComplianceChatbot/CitationModal";
import { SourceContextMenu } from "@root/components/LegalComplianceChatbot/SourceContextMenu";
import { useRiskAssessmentContext } from "@root/context/risk-assessment-context";
import {
  MAX_VISIBLE_CHIPS,
  PAGINATION_DEFAULTS,
  ChipType,
  PDF_LOADING_ACTION,
  ASSESSMENT_TYPE_TABS,
  DATA_SOURCE_TABS,
  RISK_ASSESSMENT_STATUS,
  ASSESSMENT_REVIEW_STATUSES,
  STREAMING_TIMEOUT,
} from "@root/constants/common";
import useRiskAssessment from "@root/hooks/useRiskAssessments";
import MarkdownContent from "@root/components/markdown/markdown";
import { ChatService } from "@root/services";
import { useRef } from "react";
import AssessmentSkeletonLoader from "@root/components/common/assessment-skeleton-loader";
import { fetchPdfUrl } from "@root/hooks/useRiskAssessments";

// import { RecommendationCardsContainer } from "@root/components/recommendation-cards";

const RiskAssessmentDetailPage: React.FC = () => {
  const { t } = useTranslation("common");
  const chatService = new ChatService();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source"); // "created" | "uploaded" | null
  console.log("source in risk assessment detail", source);
  const bgColor = useColorModeValue("white", "gray.800");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const [assessmentData, setAssessmentData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [menuPosition, setMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [selectedSourceItem, setSelectedSourceItem] = useState<any>(null);
  const [loadingAction, setLoadingAction] = useState<PDF_LOADING_ACTION | null>(
    null
  );
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [citedContext, setCitedContext] = useState<string>("");
  const {
    isOpen: isCitedTextModalOpen,
    onOpen: onCitedTextModalOpen,
    onClose: onCitedTextModalClose,
  } = useDisclosure();
  const { addRiskAssessment, fetchRiskAssessments, pageNo } =
    useRiskAssessmentContext();
  const { getRiskAssessmentById, riskAssessmentLoading } = useRiskAssessment();
  const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasMainContentRef = useRef(false); // Track if main content has been rendered

  // Streaming state
  const [streamedContent, setStreamedContent] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState(false);
  const streamIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Track streaming position and full content
  const streamingPositionRef = useRef(0);
  const lastKnownContentRef = useRef<string>(""); // Store the actual content string
  const assessmentDataRef = useRef(assessmentData); // Keep a ref to latest data

  const handleCloseContextMenu = useCallback(() => {
    setMenuPosition(null);
    setSelectedSourceItem(null);
  }, []);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = () => {
      if (menuPosition) {
        handleCloseContextMenu();
      }
    };

    if (menuPosition) {
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [menuPosition, handleCloseContextMenu]);

  // Fetch assessment data on component mount
  useEffect(() => {
    const fetchAssessmentData = async () => {
      if (!id) return;

      try {
        setError(null);
        const response = await getRiskAssessmentById(id);
        if (response?.data) {
          setAssessmentData(response?.data);
          // Mark main content as rendered if response exists
          if (response?.data?.response) {
            hasMainContentRef.current = true;
          }
        }
      } catch (err) {
        console.error("Error fetching assessment:", err);
        setError("Failed to load assessment data");
      }
    };

    fetchAssessmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Polling effect when status is not COMPLETED (7)
  useEffect(() => {
    if (!id || !assessmentData) return;

    const status = assessmentData?.status;
    const isUploaded = source === ASSESSMENT_TYPE_TABS.UPLOADED;

    // Use appropriate status constants based on source type
    const COMPLETED_STATUS = isUploaded
      ? ASSESSMENT_REVIEW_STATUSES.COMPLETED
      : RISK_ASSESSMENT_STATUS.COMPLETED;
    const EXCEPTION_STATUS = isUploaded
      ? ASSESSMENT_REVIEW_STATUSES.EXCEPTION
      : RISK_ASSESSMENT_STATUS.EXCEPTION;
    const IRRELEVANT_DOC_STATUS = isUploaded
      ? ASSESSMENT_REVIEW_STATUSES.IRRELEVANT_DOC
      : null; // Only uploaded assessments have irrelevant doc status

    // Stop polling if completed, exception, or irrelevant doc
    if (
      status === COMPLETED_STATUS ||
      status === EXCEPTION_STATUS ||
      (isUploaded && status === IRRELEVANT_DOC_STATUS)
    ) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Start polling if not completed
    const poll = async () => {
      try {
        const response = await getRiskAssessmentById(id);
        if (response?.data) {
          const newStatus = response?.data?.status;
          const currentStatus = assessmentData?.status;

          // Only update if status changed or if tab-specific data changed
          const shouldUpdate =
            newStatus !== currentStatus ||
            // Update if tab data changed
            (newStatus >= RISK_ASSESSMENT_STATUS.SIMILAR_ACCIDENT_LIST &&
              response?.data?.similarAccident !==
                assessmentData?.similarAccident) ||
            (newStatus >= RISK_ASSESSMENT_STATUS.SIMILAR_CHEMICAL_RECORDS &&
              (response?.data?.chemicalData !== assessmentData?.chemicalData ||
                response?.data?.chemical_data !==
                  assessmentData?.chemical_data)) ||
            (newStatus >=
              RISK_ASSESSMENT_STATUS.SAFETY_REGULATIONS_GENERATION &&
              response?.data?.lawRules !== assessmentData?.lawRules) ||
            // IMPORTANT: Update if response content changed
            response?.data?.response !== assessmentData?.response;

          if (shouldUpdate) {
            // Only update response if it's new or hasn't been displayed yet
            if (
              response?.data?.response &&
              (!hasMainContentRef.current ||
                response?.data?.response !== assessmentData?.response)
            ) {
              hasMainContentRef.current = true;
            }

            setAssessmentData((prev: any) => ({
              ...prev,
              ...response?.data,
            }));
          }

          // Stop polling if status changed to completed, exception, or irrelevant doc
          const COMPLETED_STATUS = isUploaded
            ? ASSESSMENT_REVIEW_STATUSES.COMPLETED
            : RISK_ASSESSMENT_STATUS.COMPLETED;
          const EXCEPTION_STATUS = isUploaded
            ? ASSESSMENT_REVIEW_STATUSES.EXCEPTION
            : RISK_ASSESSMENT_STATUS.EXCEPTION;
          const IRRELEVANT_DOC_STATUS = isUploaded
            ? ASSESSMENT_REVIEW_STATUSES.IRRELEVANT_DOC
            : null;

          if (
            newStatus === COMPLETED_STATUS ||
            newStatus === EXCEPTION_STATUS ||
            (isUploaded && newStatus === IRRELEVANT_DOC_STATUS)
          ) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        }
      } catch (err) {
        console.error("Error polling assessment:", err);
        // Stop polling on error
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    };

    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Start polling with 2s interval
    poll(); // Immediate call
    pollingIntervalRef.current = setInterval(poll, STREAMING_TIMEOUT);

    // Cleanup on unmount or when id/status changes
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, assessmentData?.status]);

  // Update the ref whenever assessmentData changes
  useEffect(() => {
    assessmentDataRef.current = assessmentData;
  }, [assessmentData]);

  // Streaming effect for progressive content display
  useEffect(() => {
    const isUploaded = source === ASSESSMENT_TYPE_TABS.UPLOADED;

    // Reset everything if no response
    if (!assessmentData?.response) {
      setStreamedContent("");
      setIsStreaming(false);
      streamingPositionRef.current = 0;
      lastKnownContentRef.current = "";
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }
      return;
    }

    // Parse the current full content from API
    let currentFullContent = "";
    try {
      currentFullContent = JSON.parse(assessmentData.response)?.answer || "";
    } catch (err) {
      console.error("Error parsing assessment response:", err);
      return;
    }

    const isStreamingStatus =
      isUploaded &&
      assessmentData?.status === ASSESSMENT_REVIEW_STATUSES.STREAMING;

    // For CREATED assessments, show full content immediately
    if (!isUploaded) {
      setIsStreaming(false);
      setStreamedContent(currentFullContent);
      streamingPositionRef.current = currentFullContent.length;
      lastKnownContentRef.current = currentFullContent;
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }
      return;
    }

    // For UPLOADED assessments with STREAMING status
    if (isStreamingStatus) {
      // Keep streaming indicator visible throughout STREAMING status
      setIsStreaming(true);

      // Check if this is the FIRST TIME we're seeing content (page just loaded)
      const isFirstLoad =
        streamingPositionRef.current === 0 &&
        lastKnownContentRef.current === "";

      // Check if new content has arrived from polling
      if (currentFullContent !== lastKnownContentRef.current) {
        const oldContentLength = lastKnownContentRef.current.length;
        const newContentLength = currentFullContent.length;

        // Update our reference
        lastKnownContentRef.current = currentFullContent;

        if (isFirstLoad) {
          // FIRST TIME - Show all existing content immediately, don't make user wait
          streamingPositionRef.current = newContentLength;
          setStreamedContent(currentFullContent);

          // The interval will handle streaming NEW content when polling brings it
        }
        // SUBSEQUENT UPDATES - Don't jump ahead, let interval handle smooth typewriter streaming
      }

      // Start/maintain streaming interval
      if (!streamIntervalRef.current) {
        streamIntervalRef.current = setInterval(() => {
          // Read latest data from ref
          const latestData = assessmentDataRef.current;
          if (!latestData?.response) return;

          let latestFullContent = "";
          try {
            latestFullContent = JSON.parse(latestData.response)?.answer || "";
          } catch (err) {
            console.error("Error parsing in interval:", err);
            return;
          }

          // Update reference if content changed
          if (latestFullContent !== lastKnownContentRef.current) {
            lastKnownContentRef.current = latestFullContent;
          }

          const targetLength = lastKnownContentRef.current.length;
          const currentPosition = streamingPositionRef.current;

          // Stream with consistent smooth typewriter effect
          if (currentPosition < targetLength) {
            // Fixed chunk size for consistent natural typing speed throughout
            const chunkSize = 1; // 1 char every 15ms = 67 chars/sec (smooth typewriter)

            streamingPositionRef.current = Math.min(
              targetLength,
              currentPosition + chunkSize
            );
            setStreamedContent(
              lastKnownContentRef.current.substring(
                0,
                streamingPositionRef.current
              )
            );
          }
          // When caught up, interval keeps running for next content (no pauses)
        }, 15); // Faster interval (15ms) for smoother typewriter animation
      }
    } else {
      // Not in streaming status - stop and show full content
      setIsStreaming(false);
      setStreamedContent(currentFullContent);
      streamingPositionRef.current = currentFullContent.length;
      lastKnownContentRef.current = currentFullContent;

      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }
    };
  }, [assessmentData?.response, assessmentData?.status, source]);
  // Include assessmentData?.response to detect new content from polling

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleAssessmentSubmit = async (data: AssessmentFormData) => {
    try {
      await addRiskAssessment(data);
      const payload = {
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        offset: (pageNo - 1) * PAGINATION_DEFAULTS.PAGESIZE,
      };
      await fetchRiskAssessments(payload);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error creating assessment:", error);
    }
  };

  // Function to render recommendation cards
  // const renderRecommendationCards = () => {
  //   try {
  //     const recommendationsData = JSON.parse(
  //       assessmentData?.recommendations
  //     )?.recommendations;

  //     if (!recommendationsData) return null;

  //     return (
  //       <RecommendationCardsContainer
  //         recommendationsData={recommendationsData}
  //       />
  //     );
  //   } catch (error) {
  //     console.error("Error parsing recommendations:", error);
  //     return null;
  //   }
  // };

  const closeEvidenceModal = () => {
    setIsEvidenceModalOpen(false);
  };

  // Helper function to render chips with limit and tooltip
  const renderChipsWithLimit = (
    items: any[],
    maxVisible: number = MAX_VISIBLE_CHIPS,
    type: ChipType = ChipType.MATERIALS
  ) => {
    if (!items || items?.length === 0) {
      const noItemsText =
        type === ChipType.MATERIALS
          ? t("assessments.noMaterials")
          : t("assessments.noEquipments");
      return (
        <Text fontSize="14px" color="#6B7280">
          {noItemsText}
        </Text>
      );
    }

    const visibleItems = items?.slice(0, maxVisible);
    const remainingCount = items?.length - maxVisible;

    return (
      <VStack spacing={2} align="flex-start">
        <HStack spacing={2} wrap="wrap">
          {visibleItems?.map((item: any) => (
            <Badge
              key={item.id}
              bg="#005EB8"
              color="white"
              fontSize="14px"
              px={3}
              py={1}
              borderRadius="16px"
            >
              {item?.title}
            </Badge>
          ))}
        </HStack>
        {remainingCount > 0 && (
          <Tooltip
            label={
              <Box>
                <ul style={{ margin: 0, paddingLeft: "16px" }}>
                  {items?.slice(maxVisible)?.map((item: any) => (
                    <li key={item?.id}>{item?.title}</li>
                  ))}
                </ul>
              </Box>
            }
            placement="top"
            hasArrow
          >
            <Badge
              bg="#E2E8F0"
              color="#4A5568"
              fontSize="14px"
              px={3}
              py={1}
              borderRadius="16px"
              cursor="pointer"
              _hover={{ bg: "#CBD5E0" }}
            >
              +{remainingCount} {t("assessments.remaining")}
            </Badge>
          </Tooltip>
        )}
      </VStack>
    );
  };

  // Function to render assessment details
  const renderAssessmentDetails = () => {
    if (!assessmentData || source === ASSESSMENT_TYPE_TABS.UPLOADED)
      return null;

    return (
      <Box
        bg="white"
        borderRadius="8px"
        border="1px solid #E5E7EB"
        p={6}
        mb={6}
      >
        <Text fontSize="20px" fontWeight="600" color="#1A202C" mb={4}>
          {t("assessments.assessmentDetails")}
        </Text>

        <HStack spacing={6} align="flex-start">
          {/* Name */}
          <VStack align="flex-start" spacing={2} flex="1">
            <Text fontSize="14px" fontWeight="700" color="#1A202C">
              {t("assessments.name")}
            </Text>
            <Text fontSize="16px" color="#1A202C">
              {assessmentData?.name}
            </Text>
          </VStack>

          {/* Department */}
          <VStack align="flex-start" spacing={2} flex="1">
            <Text fontSize="14px" fontWeight="700" color="#1A202C">
              {t("assessments.department")}
            </Text>
            <Badge colorScheme="blue" fontSize="14px" px={3} py={1}>
              {assessmentData?.department?.title ||
                t("assessments.noDepartment")}
            </Badge>
          </VStack>

          {/* Description */}
          <VStack align="flex-start" spacing={2} flex="1">
            <Text fontSize="14px" fontWeight="700" color="#1A202C">
              {t("assessments.description")}
            </Text>
            <Text fontSize="16px" color="#1A202C" maxW="70%">
              {assessmentData?.description || t("assessments.noDescription")}
            </Text>
          </VStack>

          {/* Materials */}
          <VStack align="flex-start" spacing={2} flex="1">
            <Text fontSize="14px" fontWeight="700" color="#1A202C">
              {t("assessments.materials")}
            </Text>
            {renderChipsWithLimit(
              assessmentData?.materials,
              MAX_VISIBLE_CHIPS,
              ChipType.MATERIALS
            )}
          </VStack>

          {/* Equipments */}
          <VStack align="flex-start" spacing={2} flex="1">
            <Text fontSize="14px" fontWeight="700" color="#1A202C">
              {t("assessments.equipments")}
            </Text>
            {renderChipsWithLimit(
              assessmentData?.equipments,
              MAX_VISIBLE_CHIPS,
              ChipType.EQUIPMENTS
            )}
          </VStack>
        </HStack>
      </Box>
    );
  };

  const handleOpenInNewTab = async () => {
    if (!selectedSourceItem || loadingAction) return;

    setLoadingAction(PDF_LOADING_ACTION.NEW_TAB);
    try {
      const fileUrl = await fetchPdfUrl(
        selectedSourceItem,
        baseAuthUrl,
        chatService
      );
      if (fileUrl) {
        window.open(fileUrl, "_blank");
      }
    } catch (error) {
      console.error("Error opening PDF:", error);
    } finally {
      setLoadingAction(null);
      handleCloseContextMenu();
    }
  };

  const handleViewCitedText = () => {
    if (selectedSourceItem?.cited_context) {
      setCitedContext(selectedSourceItem.cited_context);
    } else {
      setCitedContext("");
    }
    handleCloseContextMenu();
    onCitedTextModalOpen();
  };

  const handleLinkClick = useCallback(
    (text: string, url: string, event?: MouseEvent) => {
      // Prevent default link behavior
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (text) {
        const assessmentSources = JSON.parse(assessmentData?.sources)?.sources;
        const sourceItem = assessmentSources?.find((item: any) =>
          item?.file_name?.includes(text)
        );

        // Store the source item and show menu at click location
        setSelectedSourceItem(sourceItem);
        if (event) {
          setMenuPosition({ x: event.clientX, y: event.clientY });
        }
      }
    },
    [assessmentData]
  );

  const asssessmentContent = () => {
    // Use streamedContent if available, otherwise fall back to direct parsing
    const contentToDisplay =
      streamedContent ||
      (assessmentData?.response
        ? JSON.parse(assessmentData?.response)?.answer
        : "");

    if (assessmentData?.response || streamedContent) {
      return (
        <Box
          bg={source === ASSESSMENT_TYPE_TABS.UPLOADED ? undefined : "white"}
          color={
            source === ASSESSMENT_TYPE_TABS.UPLOADED ? "#2B3F56" : undefined
          }
          style={
            source === ASSESSMENT_TYPE_TABS.UPLOADED
              ? {
                  background:
                    "linear-gradient(180deg, #E3EBF7 0%, #B8D4F0 100%)",
                }
              : undefined
          }
          borderRadius="8px"
          border="1px solid #E5E7EB"
          p={6}
          mb={6}
          sx={{
            ...(source === ASSESSMENT_TYPE_TABS.UPLOADED && {
              "& *": {
                color: "#2B3F56 !important",
              },
              "& a": {
                color: "#1C00B8 !important",
                textDecoration: "underline",
                fontWeight: "600",
                _hover: {
                  color: "#005EB8 !important",
                },
              },
            }),
          }}
        >
          <MarkdownContent
            content={contentToDisplay}
            onLinkClick={handleLinkClick}
            colorRiskCells={true}
            disableLinks={!assessmentData?.sources}
          />
          {/* Streaming indicator */}
          {isStreaming && (
            <HStack mt={2} spacing={2}>
              <Spinner
                size="sm"
                color={
                  source === ASSESSMENT_TYPE_TABS.UPLOADED
                    ? "#2B3F56"
                    : "#005EB8"
                }
                thickness="2px"
              />
              <Text fontSize="12px" color="#6B7280" fontStyle="italic">
                {t("assessments.streaming")}
              </Text>
            </HStack>
          )}
        </Box>
      );
    } else {
      return (
        <Box
          bg="white"
          borderRadius="8px"
          border="1px solid #E5E7EB"
          p={6}
          mb={6}
        >
          <Text fontSize="16px" color="#6B7280">
            {t("assessments.noContentAvailable")}
          </Text>
        </Box>
      );
    }
  };

  // Function to parse and render chemical data
  const renderChemicalData = () => {
    const status = assessmentData?.status;
    const isUploaded = source === ASSESSMENT_TYPE_TABS.UPLOADED;
    const EXCEPTION_STATUS = isUploaded
      ? ASSESSMENT_REVIEW_STATUSES.EXCEPTION
      : RISK_ASSESSMENT_STATUS.EXCEPTION;

    // Show "no data" message if EXCEPTION status
    if (status === EXCEPTION_STATUS) {
      return (
        <Text fontSize="14px" color="#6B7280">
          {t("assessments.noChemicalData")}
        </Text>
      );
    }

    // Show skeleton until SIMILAR_CHEMICAL_RECORDS status (12) or COMPLETED (7)
    if (
      !assessmentData ||
      (status < RISK_ASSESSMENT_STATUS.SIMILAR_CHEMICAL_RECORDS &&
        status !== RISK_ASSESSMENT_STATUS.COMPLETED)
    ) {
      return <AssessmentSkeletonLoader cardCount={6} />;
    }

    try {
      const chemicalDataStr =
        assessmentData?.chemicalData || assessmentData?.chemical_data;
      if (!chemicalDataStr) {
        return (
          <Text fontSize="14px" color="#6B7280">
            {t("assessments.noChemicalData")}
          </Text>
        );
      }

      const parsedData = JSON.parse(chemicalDataStr);
      const chemicals = parsedData?.chemical_data || parsedData;

      if (!chemicals || !Array.isArray(chemicals) || !chemicals.length) {
        return (
          <Text fontSize="14px" color="#6B7280">
            {t("assessments.noChemicalData")}
          </Text>
        );
      }

      return (
        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          spacing={4}
          alignItems="stretch"
        >
          {chemicals?.map((chemical: string, index: number) => (
            <Box
              key={index}
              borderRadius="8px"
              bg="#F5F5F5"
              border="1px solid #E5E7EB"
              p={6}
            >
              <MarkdownContent content={chemical} p={0} />
            </Box>
          ))}
        </SimpleGrid>
      );
    } catch (error) {
      console.error("Error parsing chemical data:", error);
      return (
        <Text fontSize="14px" color="#6B7280">
          {t("assessments.errorLoadingChemicalData")}
        </Text>
      );
    }
  };

  // Function to parse and render similar accidents
  const renderSimilarAccidents = () => {
    const status = assessmentData?.status;
    const isUploaded = source === ASSESSMENT_TYPE_TABS.UPLOADED;
    const EXCEPTION_STATUS = isUploaded
      ? ASSESSMENT_REVIEW_STATUSES.EXCEPTION
      : RISK_ASSESSMENT_STATUS.EXCEPTION;

    // Show "no data" message if EXCEPTION status
    if (status === EXCEPTION_STATUS) {
      return (
        <Text fontSize="14px" color="#6B7280">
          {t("assessments.noSimilarAccidents")}
        </Text>
      );
    }

    // Show skeleton until SIMILAR_ACCIDENT_LIST status (9) or COMPLETED (7)
    if (
      !assessmentData ||
      (status < RISK_ASSESSMENT_STATUS.SIMILAR_ACCIDENT_LIST &&
        status !== RISK_ASSESSMENT_STATUS.COMPLETED)
    ) {
      return <AssessmentSkeletonLoader cardCount={6} />;
    }

    try {
      const accidents = JSON.parse(
        assessmentData?.similarAccident
      )?.similar_accident;

      if (!accidents || !Array.isArray(accidents) || !accidents.length) {
        return (
          <Text fontSize="14px" color="#6B7280">
            {t("assessments.noSimilarAccidents")}
          </Text>
        );
      }

      return (
        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          spacing={4}
          alignItems="stretch"
        >
          {accidents?.map((accident: string, index: number) => (
            <Box
              key={index}
              borderRadius="8px"
              bg="#F5F5F5"
              border="1px solid #E5E7EB"
              p={6}
            >
              <MarkdownContent content={accident} p={0} />
            </Box>
          ))}
        </SimpleGrid>
      );
    } catch (error) {
      console.error("Error parsing similar accidents:", error);
      return (
        <Text fontSize="14px" color="#6B7280">
          {t("assessments.errorLoadingSimilarAccidents")}
        </Text>
      );
    }
  };
  const renderSafetyRegulations = () => {
    const status = assessmentData?.status;
    const isUploaded = source === ASSESSMENT_TYPE_TABS.UPLOADED;
    const EXCEPTION_STATUS = isUploaded
      ? ASSESSMENT_REVIEW_STATUSES.EXCEPTION
      : RISK_ASSESSMENT_STATUS.EXCEPTION;

    // Show "no data" message if EXCEPTION status
    if (status === EXCEPTION_STATUS) {
      return (
        <Text fontSize="14px" color="#6B7280">
          {t("assessments.noSafetyRegulations")}
        </Text>
      );
    }

    // Show skeleton until SAFETY_REGULATIONS_GENERATION status (15) or COMPLETED (7)
    if (
      !assessmentData ||
      (status < RISK_ASSESSMENT_STATUS.SAFETY_REGULATIONS_GENERATION &&
        status !== RISK_ASSESSMENT_STATUS.COMPLETED)
    ) {
      return <AssessmentSkeletonLoader cardCount={6} />;
    }

    try {
      const laws = JSON.parse(assessmentData?.lawRules)?.law_rules;

      if (!laws || !Array.isArray(laws) || !laws.length) {
        return (
          <Text fontSize="14px" color="#6B7280">
            {t("assessments.noSafetyRegulations")}
          </Text>
        );
      }

      return (
        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          spacing={4}
          alignItems="stretch"
        >
          {laws?.map((law: string, index: number) => (
            <Box
              key={index}
              borderRadius="8px"
              bg="#F5F5F5"
              border="1px solid #E5E7EB"
              p={6}
            >
              <MarkdownContent content={law} p={0} />
            </Box>
          ))}
        </SimpleGrid>
      );
    } catch (error) {
      console.error("Error parsing safety regulations:", error);
      return (
        <Text fontSize="14px" color="#6B7280">
          {t("assessments.errorLoadingSafetyRegulations")}
        </Text>
      );
    }
  };

  // Function to get the count of cards for the selected tab
  const getTabSpecificCount = (tabIndex: number): number => {
    try {
      switch (tabIndex) {
        case DATA_SOURCE_TABS[0].index: // Incident History
          if (!assessmentData?.similarAccident) return 0;
          const accidents = JSON.parse(
            assessmentData?.similarAccident
          )?.similar_accident;
          return Array.isArray(accidents) ? accidents.length : 0;

        case DATA_SOURCE_TABS[1].index: // Chemical Database
          const chemicalDataStr =
            assessmentData?.chemicalData || assessmentData?.chemical_data;
          if (!chemicalDataStr) return 0;
          const parsedChemicalData = JSON.parse(chemicalDataStr);
          const chemicals =
            parsedChemicalData?.chemical_data || parsedChemicalData;
          return Array.isArray(chemicals) ? chemicals.length : 0;

        case DATA_SOURCE_TABS[2].index: // Safety Regulations
          if (!assessmentData?.lawRules) return 0;
          const laws = JSON.parse(assessmentData?.lawRules)?.law_rules;
          return Array.isArray(laws) ? laws.length : 0;

        default:
          return 0;
      }
    } catch (error) {
      console.error("Error getting tab count:", error);
      return 0;
    }
  };

  // Render loading state - only show on initial load, not during polling
  const shouldShowLoader = riskAssessmentLoading && !assessmentData;
  if (shouldShowLoader) {
    return (
      <Box bg={bgColor} minH="100vh" p={8}>
        <Flex justify="center" align="center" minH="60vh">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="#005EB8"
            size="xl"
          />
        </Flex>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box bg={bgColor} minH="100vh" p={8}>
        <Flex justify="center" align="center" minH="60vh">
          <Text fontSize="18px" color="red.500">
            {error}
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" p={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        {source === ASSESSMENT_TYPE_TABS.CREATED ? (
          <Text fontSize="28px" fontWeight="700" textColor="#1A202C">
            {t("riskAssessmentDetail.aiRecommendation")}
          </Text>
        ) : (
          ""
        )}
      </Flex>

      {/* Assessment Details */}
      {renderAssessmentDetails()}

      {/* Assessment Content */}
      {asssessmentContent()}

      {/* Integrated Data Sources Section - Only for CREATED assessments */}
      {source === ASSESSMENT_TYPE_TABS.CREATED && (
        <Box mt={12}>
          {/* Header with Status */}
          <Flex justify="space-between" align="center" mb={6}>
            <Text fontSize="28px" fontWeight="700" textColor="#1A202C">
              {t("riskAssessmentDetail.integratedDataSources")}
            </Text>
            <HStack spacing={2}>
              <Box w="8px" h="8px" borderRadius="50%" bg="#3AB65A" />
              <Text fontSize="14px" color="#6B7280">
                {`${getTabSpecificCount(selectedTabIndex)} ${t(
                  "riskAssessmentDetail.sourcesConnected"
                )}`}
              </Text>
            </HStack>
          </Flex>

          {/* Tabs Navigation */}
          <Tabs
            variant="unstyled"
            index={selectedTabIndex}
            onChange={setSelectedTabIndex}
          >
            <TabList borderBottom="2px solid #E5E7EB" gap={4}>
              {DATA_SOURCE_TABS.map((tab) => {
                const tabKey = tab.label.toLowerCase().replace(/\s+/g, "");
                const translationKey =
                  tabKey === "chemicaldatabase"
                    ? "assessments.dataSourceTabs.chemicalDatabase"
                    : tabKey === "incidenthistory"
                    ? "assessments.dataSourceTabs.incidentHistory"
                    : tabKey === "safetyregulations"
                    ? "assessments.dataSourceTabs.safetyRegulations"
                    : tab.label;
                return (
                  <Tab
                    key={tab.label}
                    _selected={{
                      color: "#005EB8",
                      borderBottom: "2px solid #005EB8",
                      fontWeight: "600",
                    }}
                    color="#6B7280"
                    fontSize="16px"
                    pb={3}
                    px={0}
                    mr={tab.index < DATA_SOURCE_TABS.length - 1 ? 6 : 0}
                  >
                    {t(translationKey)}
                  </Tab>
                );
              })}
            </TabList>

            <TabPanels mt={6}>
              <TabPanel px={0}>
                {/* Incident History Cards */}
                {renderSimilarAccidents()}
              </TabPanel>
              <TabPanel px={0}>
                {/* Chemical Database Cards */}
                {renderChemicalData()}
              </TabPanel>
              <TabPanel px={0}>
                {/* Safety Regulations Cards */}
                {renderSafetyRegulations()}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      )}

      {/* AI Recommendations */}
      {/* TODO: Will add later */}
      {/* {renderRecommendationCards()} */}

      {/* Create Assessment Modal */}
      <CreateAssessmentModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleAssessmentSubmit}
      />

      {/* Evidence Modal */}
      <EvidenceReviewModal
        isOpen={isEvidenceModalOpen}
        onClose={closeEvidenceModal}
      />

      {/* Context Menu */}
      <SourceContextMenu
        menuPosition={menuPosition}
        selectedSourceItem={selectedSourceItem}
        loadingAction={loadingAction}
        onOpenInNewTab={handleOpenInNewTab}
        onViewCitedText={handleViewCitedText}
        onClose={handleCloseContextMenu}
      />
      {/* Cited Text Reference Modal */}
      <CitationModal
        isOpen={isCitedTextModalOpen}
        onClose={() => {
          onCitedTextModalClose();
          setCitedContext("");
        }}
        citedContext={citedContext}
      />
      {/* PDF Viewer Modal */}
      <PdfViewerModal
        isOpen={isPdfModalOpen}
        onClose={() => {
          setIsPdfModalOpen(false);
          setPdfUrl("");
        }}
        pdfUrl={pdfUrl}
      />
    </Box>
  );
};

export default RiskAssessmentDetailPage;
